import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  RentalListing,
  RentalLead,
  RentalSyndication,
  PLATFORMS,
  isListingDeletable,
} from "@/hooks/use-rentals";
import {
  X,
  Users,
  Building2,
  CalendarDays,
  CheckCircle2,
  XCircle,
  Pencil,
  RotateCcw,
  Trash2,
} from "lucide-react";

// ── Helpers ────────────────────────────────────────────────────────────────
const statusDot: Record<string, string> = {
  active:   "bg-green-500 shadow-[0_0_0_3px_rgba(34,197,94,0.18)]",
  draft:    "bg-muted-foreground/60",
  paused:   "bg-amber-400 shadow-[0_0_0_3px_rgba(251,191,36,0.18)]",
  rented:   "bg-blue-500 shadow-[0_0_0_3px_rgba(59,130,246,0.18)]",
  archived: "bg-muted-foreground/40",
};
const statusLabel: Record<string, string> = {
  active: "Live", draft: "Draft", paused: "Paused", rented: "Rented", archived: "Archived",
};

const leadStatusStyle: Record<string, { cls: string; label: string }> = {
  new:               { cls: "bg-blue-500/15 text-blue-400 border-blue-500/28",       label: "NEW" },
  contacted:         { cls: "bg-amber-400/15 text-amber-400 border-amber-400/28",    label: "CONTACTED" },
  showing_scheduled: { cls: "bg-green-500/15 text-green-400 border-green-500/28",    label: "SCHEDULED" },
  application_sent:  { cls: "bg-primary/15 text-primary border-primary/28",          label: "APPLIED" },
  approved:          { cls: "bg-green-500/15 text-green-400 border-green-500/28",    label: "APPROVED" },
  denied:            { cls: "bg-destructive/15 text-destructive border-destructive/28", label: "DECLINED" },
  lost:              { cls: "bg-muted text-muted-foreground border-border",           label: "LOST" },
};

const platformPip: Record<string, { bg: string; initial: string; label: string }> = {
  zillow:         { bg: "bg-[#006aff]", initial: "Z", label: "Zillow" },
  apartments_com: { bg: "bg-[#00838f]", initial: "A", label: "Apartments.com" },
  realtor_com:    { bg: "bg-[#d92228]", initial: "R", label: "Realtor.com" },
  trulia:         { bg: "bg-[#00b04f]", initial: "T", label: "Trulia" },
  rent_com:       { bg: "bg-[#f5a623]", initial: "R", label: "Rent.com" },
  facebook:       { bg: "bg-[#1877f2]", initial: "F", label: "Facebook" },
  craigslist:     { bg: "bg-[#5d2da9]", initial: "C", label: "Craigslist" },
};

const amenityLabels: Record<string, string> = {
  laundry: "In-unit laundry", ac: "Central A/C", dishwasher: "Dishwasher",
  hardwood: "Hardwood floors", updatedKitch: "Updated kitchen", updatedBath: "Updated bath",
  garage: "Garage", yard: "Yard", fenced: "Fenced yard", basement: "Basement", patio: "Patio / deck",
};

const utilityLabels: Record<string, string> = {
  water: "Water", gas: "Gas", electric: "Electric", trash: "Trash", internet: "Internet",
};

function fmt(n: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD", maximumFractionDigits: 0,
  }).format(n);
}

function fmtDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso + "T12:00:00").toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
  });
}

function fmtRelDays(iso: string | null | undefined) {
  if (!iso) return "—";
  const days = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000));
  return days === 0 ? "today" : days === 1 ? "yesterday" : `${days} days ago`;
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10.5px] font-bold uppercase tracking-[0.06em] text-muted-foreground mb-3">
      {children}
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between items-center text-[13px] py-2 border-b border-border last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold text-foreground">{value}</span>
    </div>
  );
}

// ── Props ──────────────────────────────────────────────────────────────────
interface Props {
  listing: RentalListing | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leads: RentalLead[];
  syndications: RentalSyndication[];
  propData: any;
  onViewApplications: () => void;
  onEdit: () => void;
  onUpdateStatus: (status: RentalListing["status"]) => void;
  onRemoveFromRent: () => void;
  /** Opens the delete confirmation. Hidden while the unit is rented. */
  onDelete: () => void;
}

export function ListingDetailSheet({
  listing,
  open,
  onOpenChange,
  leads,
  syndications,
  propData,
  onViewApplications,
  onEdit,
  onUpdateStatus,
  onRemoveFromRent,
  onDelete,
}: Props) {
  if (!listing) return null;

  const addr1 = propData?.address || propData?.street_address || listing.title;
  const addr2 = [propData?.city, propData?.state].filter(Boolean).join(", ");
  const heroPhoto = listing.photos?.[0];

  // Prefer the listing's own specs, fall back to the linked property's data.
  const beds  = listing.beds  ?? propData?.bedrooms   ?? propData?.beds   ?? null;
  const baths = listing.baths ?? propData?.bathrooms  ?? propData?.baths  ?? null;
  const sqft  = listing.sqft  ?? propData?.square_feet ?? propData?.sqft  ?? null;
  const leaseLabel = listing.lease_term_months === 0
    ? "M2M"
    : listing.lease_term_months
      ? `${listing.lease_term_months} mo`
      : "—";

  const publishedSyns = syndications.filter(
    (s) => s.listing_id === listing.id && s.status === "published",
  );
  const listingLeads = leads.filter((l) => l.listing_id === listing.id);
  const newLeadCount = listingLeads.filter((l) => l.status === "new").length;

  const canDelete = isListingDeletable(listing);

  const getPlatformName = (id: string) => PLATFORMS.find((p) => p.id === id)?.name || id;

  const datedLabel = listing.status === "draft"
    ? "Saved " + fmtRelDays(listing.updated_at)
    : "Listed " + fmtRelDays(listing.created_at);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full max-w-[480px] p-0 flex flex-col gap-0 border-l border-border"
        hideCloseButton
      >
        {/* ── Hero ── */}
        <div className="relative h-[220px] bg-muted flex-shrink-0 border-b border-border">
          {heroPhoto ? (
            <img src={heroPhoto} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Building2 className="w-16 h-16 text-muted-foreground/20" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-[rgba(5,6,8,0.05)] to-[rgba(5,6,8,0.78)] pointer-events-none" />

          {/* Status pill */}
          <span className="absolute top-3 left-3 z-10 flex items-center gap-1.5 rounded-full px-2.5 py-[5px] text-[11.5px] font-semibold bg-background/70 backdrop-blur-sm border border-white/10 text-foreground">
            <span className={cn("w-[7px] h-[7px] rounded-full flex-shrink-0", statusDot[listing.status] ?? statusDot.draft)} />
            {statusLabel[listing.status] ?? listing.status}
          </span>

          {/* Close */}
          <button
            onClick={() => onOpenChange(false)}
            className="absolute top-3 right-3 z-10 w-8 h-8 rounded-md grid place-items-center bg-background/70 backdrop-blur-sm border border-white/10 text-foreground hover:bg-background/90 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Address + rent */}
          <div className="absolute left-[18px] right-[18px] bottom-4 z-10 flex items-end justify-between gap-3">
            <div className="min-w-0">
              <div
                className="text-[19px] font-bold leading-snug text-foreground"
                style={{ textShadow: "0 1px 2px rgba(0,0,0,0.5)" }}
              >
                {addr1}
              </div>
              {addr2 && (
                <div
                  className="text-[12.5px] mt-1"
                  style={{ color: "rgba(255,255,255,0.78)", textShadow: "0 1px 2px rgba(0,0,0,0.5)" }}
                >
                  {addr2}
                </div>
              )}
            </div>
            <span className="flex-shrink-0 rounded-full px-3 py-[5px] text-[12.5px] font-bold bg-background/70 backdrop-blur-sm border border-white/10 text-foreground whitespace-nowrap">
              {fmt(listing.rent_amount)}
              <span className="text-white/70 font-medium text-[11.5px]"> /mo</span>
            </span>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto px-[22px] py-[18px] flex flex-col gap-[22px]">

          {/* 4-tile stat row */}
          <div className="grid grid-cols-4 gap-2">
            {[
              { label: "Beds",  value: beds  ?? "—" },
              { label: "Baths", value: baths ?? "—" },
              { label: "Sqft",  value: sqft  ?? "—" },
              { label: "Lease", value: leaseLabel },
            ].map(({ label, value }) => (
              <div
                key={label}
                className="bg-muted border border-border rounded-lg p-3"
              >
                <div className="text-[11px] text-muted-foreground font-semibold mb-1">
                  {label}
                </div>
                <div className="text-[17px] font-extrabold text-foreground leading-tight">
                  {value}
                </div>
              </div>
            ))}
          </div>

          {/* About this listing */}
          {(listing.title || listing.description) && (
            <section>
              <SectionLabel>About this listing</SectionLabel>
              {listing.title && (
                <div className="text-[14px] font-semibold text-foreground mb-1.5">
                  {listing.title}
                </div>
              )}
              {listing.description && (
                <div className="text-[13px] text-foreground leading-relaxed">
                  {listing.description}
                </div>
              )}
            </section>
          )}

          {/* Terms */}
          <section>
            <SectionLabel>Terms</SectionLabel>
            <InfoRow label="Monthly rent"     value={fmt(listing.rent_amount)} />
            {listing.security_deposit != null && (
              <InfoRow label="Security deposit" value={fmt(listing.security_deposit)} />
            )}
            <InfoRow
              label="Lease length"
              value={
                listing.lease_term_months === 0
                  ? "Month-to-month"
                  : listing.lease_term_months
                    ? `${listing.lease_term_months} months`
                    : "—"
              }
            />
            {listing.available_date && (
              <InfoRow label="Available from" value={fmtDate(listing.available_date)} />
            )}
            {listing.pet_policy && (
              <InfoRow label="Pets" value={listing.pet_policy} />
            )}
          </section>

          {/* Amenities */}
          {listing.amenities && listing.amenities.length > 0 && (
            <section>
              <SectionLabel>Amenities</SectionLabel>
              <div className="flex flex-wrap gap-1.5">
                {listing.amenities.map((a) => (
                  <span
                    key={a}
                    className="text-[11.5px] font-medium px-2.5 py-1 rounded-full bg-muted border border-border text-muted-foreground"
                  >
                    {amenityLabels[a] ?? a}
                  </span>
                ))}
              </div>
            </section>
          )}

          {/* Utilities */}
          {listing.utilities_included && listing.utilities_included.length > 0 && (
            <section>
              <SectionLabel>Utilities included in rent</SectionLabel>
              <div className="flex flex-wrap gap-1.5">
                {listing.utilities_included.map((u) => (
                  <span
                    key={u}
                    className="text-[11.5px] font-medium px-2.5 py-1 rounded-full bg-muted border border-border text-muted-foreground"
                  >
                    {utilityLabels[u] ?? u}
                  </span>
                ))}
              </div>
            </section>
          )}

          {/* Screening criteria */}
          <section>
            <SectionLabel>Screening criteria</SectionLabel>
            {/* Credit score / income don't apply to Section 8 — the voucher
                covers rent — so they're hidden when vouchers are accepted. */}
            {!listing.accept_section8_voucher && (
              <>
                {listing.minimum_credit_score != null && (
                  <InfoRow
                    label="Minimum credit score"
                    value={`${listing.minimum_credit_score}+`}
                  />
                )}
                {listing.income_to_rent_ratio != null && (
                  <InfoRow
                    label="Income requirement"
                    value={`${listing.income_to_rent_ratio}× monthly rent`}
                  />
                )}
              </>
            )}
            <InfoRow
              label="Background check"
              value={
                listing.background_check ? (
                  <span className="flex items-center gap-1.5 text-green-400">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Required
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 text-muted-foreground">
                    <XCircle className="w-3.5 h-3.5" /> Not required
                  </span>
                )
              }
            />
            <InfoRow
              label="Section 8 vouchers"
              value={
                listing.accept_section8_voucher ? (
                  <span className="flex items-center gap-1.5 text-green-400">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Accepted
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 text-muted-foreground">
                    <XCircle className="w-3.5 h-3.5" /> Not accepted
                  </span>
                )
              }
            />
          </section>

          {/* Syndicated to */}
          {publishedSyns.length > 0 && (
            <section>
              <SectionLabel>Syndicated to</SectionLabel>
              <div className="flex items-center gap-2 flex-wrap">
                {publishedSyns.map((s) => {
                  const pip = platformPip[s.platform];
                  return pip ? (
                    <span
                      key={s.id}
                      title={pip.label}
                      className={cn(
                        "inline-grid place-items-center w-[22px] h-[22px] rounded-[5px] text-[11px] font-black text-white flex-shrink-0",
                        pip.bg,
                      )}
                    >
                      {pip.initial}
                    </span>
                  ) : null;
                })}
              </div>
            </section>
          )}

          {/* Applications */}
          <section>
            <SectionLabel>
              Applications
              {listingLeads.length > 0 && (
                <span className="ml-1 normal-case font-semibold">
                  · {listingLeads.length}
                  {newLeadCount > 0 && (
                    <span className="text-blue-400 ml-1">, {newLeadCount} new</span>
                  )}
                </span>
              )}
            </SectionLabel>

            {listingLeads.length === 0 ? (
              <div className="text-[13px] text-muted-foreground py-3.5 text-center border border-dashed border-border rounded-lg">
                No applications yet.
              </div>
            ) : (
              <div className="space-y-2">
                {listingLeads.slice(0, 4).map((lead) => {
                  const initials = lead.name
                    .split(" ")
                    .map((p) => p[0])
                    .filter(Boolean)
                    .join("")
                    .slice(0, 2)
                    .toUpperCase();
                  const statusStyle = leadStatusStyle[lead.status];
                  const subText = [
                    lead.email ? lead.email : null,
                    lead.platform ? getPlatformName(lead.platform) : null,
                    fmtRelDays(lead.created_at),
                  ]
                    .filter(Boolean)
                    .join(" · ");

                  return (
                    <div
                      key={lead.id}
                      className="grid grid-cols-[32px_1fr_auto] gap-3 items-center p-2.5 rounded-lg bg-muted border border-border hover:border-border/70 cursor-pointer transition-colors"
                    >
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 text-white grid place-items-center text-[12px] font-bold flex-shrink-0">
                        {initials}
                      </div>
                      <div className="min-w-0">
                        <div className="text-[13px] font-semibold text-foreground">
                          {lead.name}
                        </div>
                        <div className="text-[11.5px] text-muted-foreground mt-0.5 truncate">
                          {subText}
                        </div>
                      </div>
                      {statusStyle && (
                        <span
                          className={cn(
                            "text-[9.5px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border flex-shrink-0 flex items-center gap-1",
                            statusStyle.cls,
                          )}
                        >
                          <span className="w-1 h-1 rounded-full bg-current" />
                          {statusStyle.label}
                        </span>
                      )}
                    </div>
                  );
                })}
                {listingLeads.length > 4 && (
                  <p className="text-[12px] text-muted-foreground text-center pt-1">
                    +{listingLeads.length - 4} more — click "View applications" to see all
                  </p>
                )}
              </div>
            )}
          </section>

          {/* Listed date */}
          <div className="flex items-center gap-1.5 text-[12px] text-muted-foreground pb-2">
            <CalendarDays className="w-3.5 h-3.5" />
            {datedLabel}
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="flex flex-col gap-2 px-[22px] py-3.5 border-t border-border bg-card flex-shrink-0">
          {listing.status === "rented" && (
            <Button
              variant="outline"
              className="gap-1.5 w-full border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={() => { onRemoveFromRent(); onOpenChange(false); }}
            >
              <RotateCcw className="w-4 h-4" /> Remove from rent
            </Button>
          )}
          <div className="flex justify-between gap-2">
            <Button
              variant="outline"
              className="gap-1.5 flex-1"
              onClick={() => { onViewApplications(); onOpenChange(false); }}
            >
              <Users className="w-4 h-4" /> View applications
            </Button>
            <Button
              className="gap-1.5 flex-1 bg-destructive hover:bg-destructive/90"
              onClick={() => { onEdit(); onOpenChange(false); }}
            >
              <Pencil className="w-4 h-4" /> Edit listing
            </Button>
            {/* Permanent delete — never offered on a rented unit, which has to
                come off rent first. */}
            {canDelete && (
              <Button
                variant="outline"
                aria-label="Delete listing"
                title="Delete listing"
                className="w-10 px-0 flex-shrink-0 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                onClick={() => { onDelete(); onOpenChange(false); }}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
